import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowUpIcon,
  CheckCircleIcon,
  CircleIcon,
  DotsVerticalIcon,
  EyeOffIcon,
  PlusIcon,
  User01Icon,
} from '@untitledui-icons/react/line'
import { Button as AriaButton, MenuItem as AriaMenuItem } from 'react-aria-components'
import { Dropdown } from '@/components/base/dropdown/dropdown'
import { LoadingSpinner } from '@/components/common'
import { useIdentity } from '@/hooks/api'
import {
  useConversationNotes,
  useCreateReply,
  useUpdateNote,
  useUpdateReply,
} from '@/hooks/api/useConversationNotes'
import { useAssignmentTargets } from '@/hooks/api/useUsers'
import { formatDistanceToNow, parseUTCTimestamp } from '@/lib/dateFormat'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { CreateNoteModal } from './CreateNoteModal'
import { NotePriorityIcon } from './NotePriorityIcon'
import type { Note, NoteReply } from '@/types/conversation'

// Characters after which a note body gets a "Show more" toggle
const CLAMP_LENGTH = 150
// Reply composer grows with wrapped text, then scrolls inside the field
const REPLY_MAX_LINES = 4

interface InboxNotesProps {
  conversationId: string | number
  customerId?: number
}

type Author = Note['created_by_user']

function authorName(author: Author, fallback: string): string {
  return author ? `${author.first_name} ${author.last_name}`.trim() : fallback
}

function NoteHeader({
  name,
  createdAt,
  isReply,
  isMuted,
  children,
}: {
  name: string
  createdAt: string
  isReply?: boolean
  isMuted?: boolean
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white',
            isReply ? 'bg-violet-400' : 'bg-amber-600'
          )}
          aria-hidden="true"
        >
          {name.charAt(0).toUpperCase() || '?'}
        </span>
        <span
          className={cn(
            'truncate text-sm font-medium',
            isMuted ? 'text-gray-500' : 'text-gray-900 dark:text-gray-100'
          )}
        >
          {name}
        </span>
        <span className="text-xs whitespace-nowrap text-gray-600 dark:text-gray-400">
          {formatDistanceToNow(parseUTCTimestamp(createdAt))}
        </span>
      </div>
      {children}
    </div>
  )
}

function NoteBody({
  text,
  maxLines,
  isMuted,
}: {
  text: string
  maxLines: 1 | 3
  isMuted?: boolean
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const isLong = text.length > (maxLines === 1 ? 48 : CLAMP_LENGTH)

  return (
    <div className="flex flex-col gap-1">
      <p
        className={cn(
          'text-sm wrap-anywhere whitespace-pre-line',
          isMuted ? 'text-gray-500' : 'text-gray-900 dark:text-gray-100',
          isLong && !isOpen && (maxLines === 1 ? 'line-clamp-1' : 'line-clamp-3')
        )}
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="cursor-pointer self-start text-xs font-medium text-blue-700 hover:underline dark:text-blue-400"
        >
          {isOpen
            ? t('resources.conversations.inbox.notes.show_less')
            : t('resources.conversations.inbox.notes.show_more')}
        </button>
      )}
    </div>
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 py-0.5 pr-2 pl-1.5 text-xs font-medium whitespace-nowrap text-gray-700 ring-1 ring-gray-200 ring-inset dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
      {children}
    </span>
  )
}

function EditMenu({
  label,
  triggerLabel,
  onEdit,
}: {
  label: string
  triggerLabel: string
  onEdit: () => void
}) {
  return (
    <Dropdown.Root>
      <AriaButton
        aria-label={triggerLabel}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 outline-hidden hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <DotsVerticalIcon className="size-4.5" aria-hidden="true" />
      </AriaButton>
      <Dropdown.Popover
        placement="left top"
        offset={8}
        className="w-max min-w-28 bg-white! p-1 shadow-lg ring-gray-200! dark:bg-gray-900! dark:ring-gray-700!"
      >
        <Dropdown.Menu className="py-0" selectionMode="none" disallowEmptySelection={false}>
          <AriaMenuItem
            id="edit"
            textValue={label}
            onAction={onEdit}
            className={(state) =>
              cn(
                'cursor-pointer rounded-md px-2.5 py-2 text-left text-sm font-medium text-gray-700 outline-hidden dark:text-gray-200',
                state.isHovered && 'bg-gray-50 dark:bg-gray-800'
              )
            }
          >
            {label}
          </AriaMenuItem>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

function ReplyBlock({ reply, canEdit }: { reply: NoteReply; canEdit: boolean }) {
  const { t } = useTranslation()
  const updateReply = useUpdateReply()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(reply.content)
  const editRef = useRef<HTMLTextAreaElement>(null)

  // The menu restores focus as it closes, after autoFocus has already run.
  // Place the caret once that settles, at the end of the reply.
  useLayoutEffect(() => {
    if (!isEditing) return
    const placeCaret = () => {
      const element = editRef.current
      if (!element) return
      element.focus()
      const end = element.value.length
      element.setSelectionRange(end, end)
      element.scrollTop = element.scrollHeight
    }
    placeCaret()
    const frame = requestAnimationFrame(placeCaret)
    const timer = window.setTimeout(placeCaret, 0)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [isEditing])

  const handleSave = () => {
    const content = draft.trim()
    if (!content || content === reply.content) {
      setIsEditing(false)
      return
    }
    updateReply.mutate(
      { replyId: reply.id, content, is_private: reply.is_private },
      {
        onSuccess: () => {
          toast.success(t('resources.conversation_notes.messages.reply_updated'))
          setIsEditing(false)
        },
        onError: () => toast.error(t('resources.conversations.inbox.notes.reply_update_failed')),
      }
    )
  }

  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 px-3 py-4 dark:border-gray-800">
      <NoteHeader
        name={authorName(
          reply.created_by_user,
          t('resources.conversations.user_fallback', { id: reply.created_by })
        )}
        createdAt={reply.created_at}
        isReply
      >
        {canEdit && !isEditing && (
          <EditMenu
            label={t('resources.generic.edit')}
            triggerLabel={t('resources.conversations.inbox.notes.more_actions')}
            onEdit={() => {
              setDraft(reply.content)
              setIsEditing(true)
            }}
          />
        )}
      </NoteHeader>
      {isEditing ? (
        <div className="flex items-end gap-2">
          <textarea
            ref={editRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => event.key === 'Escape' && setIsEditing(false)}
            aria-label={t('resources.generic.edit')}
            className="h-20 min-w-0 flex-1 resize-none bg-transparent text-sm text-gray-900 outline-hidden! dark:text-gray-100"
          />
          <SendButton
            label={t('resources.generic.save')}
            onPress={handleSave}
            isDisabled={updateReply.isPending}
          />
        </div>
      ) : (
        <NoteBody text={reply.content} maxLines={3} />
      )}
    </div>
  )
}

function ReplyComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  isDisabled,
  sendLabel,
}: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  isDisabled?: boolean
  sendLabel: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.style.height = 'auto'
    const style = getComputedStyle(element)
    const lineHeight = Number.parseFloat(style.lineHeight)
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return
    const padding = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom)
    const maxHeight = lineHeight * REPLY_MAX_LINES + padding
    const fits = element.scrollHeight <= maxHeight
    element.style.height = `${fits ? element.scrollHeight : maxHeight}px`
    element.style.overflowY = fits ? 'hidden' : 'auto'
  }, [value])

  return (
    <div className="flex items-end gap-2 px-3 py-2">
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault()
            onSubmit()
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 resize-none bg-transparent py-1 text-sm leading-5 wrap-anywhere text-gray-900 outline-hidden! placeholder:text-gray-400 dark:text-gray-100"
      />
      <SendButton label={sendLabel} onPress={onSubmit} isDisabled={isDisabled} />
    </div>
  )
}

function SendButton({
  label,
  onPress,
  isDisabled,
}: {
  label: string
  onPress: () => void
  isDisabled?: boolean
}) {
  return (
    <AriaButton
      aria-label={label}
      onPress={onPress}
      isDisabled={isDisabled}
      className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-900 text-white disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900"
    >
      <ArrowUpIcon className="size-4" aria-hidden="true" />
    </AriaButton>
  )
}

function NoteCard({ note, currentUserId }: { note: Note; currentUserId?: number }) {
  const { t } = useTranslation()
  const updateNote = useUpdateNote()
  const createReply = useCreateReply()
  const [reply, setReply] = useState('')
  const isClosed = note.status === 'closed'

  const assigneeName = note.assigned_to_user
    ? authorName(note.assigned_to_user, '')
    : note.assigned_team?.name

  const handleToggle = () => {
    updateNote.mutate(
      { id: note.id, status: isClosed ? 'open' : 'closed' },
      { onError: () => toast.error(t('resources.conversations.inbox.notes.update_failed')) }
    )
  }

  const handleReply = () => {
    const content = reply.trim()
    if (!content) return
    createReply.mutate(
      { noteId: note.id, content, close_note: false },
      {
        onSuccess: () => setReply(''),
        onError: () => toast.error(t('resources.conversation_notes.reply_failed')),
      }
    )
  }

  return (
    <article className="w-full overflow-hidden rounded-2xl ring-1 ring-gray-200 ring-inset dark:ring-gray-800">
      <div className="flex flex-col gap-2 border-b border-gray-100 px-3 py-4 dark:border-gray-800">
        <div className="flex flex-col gap-3">
          <NoteHeader
            name={authorName(
              note.created_by_user,
              t('resources.conversations.user_fallback', { id: note.created_by })
            )}
            createdAt={note.created_at}
            isMuted={isClosed}
          >
            <button
              type="button"
              onClick={handleToggle}
              disabled={updateNote.isPending}
              aria-pressed={isClosed}
              aria-label={
                isClosed
                  ? t('resources.conversations.inbox.notes.reopen')
                  : t('resources.conversations.inbox.notes.resolve')
              }
              title={
                isClosed
                  ? t('resources.conversations.inbox.notes.reopen')
                  : t('resources.conversations.inbox.notes.resolve')
              }
              className="cursor-pointer text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
            >
              {isClosed ? (
                <CheckCircleIcon className="size-4.5" aria-hidden="true" />
              ) : (
                <CircleIcon className="size-4.5" aria-hidden="true" />
              )}
            </button>
          </NoteHeader>
          <NoteBody text={note.content} maxLines={isClosed ? 1 : 3} isMuted={isClosed} />
        </div>
        {(note.is_private || (!isClosed && (note.priority || assigneeName))) && (
          <div className="flex flex-wrap gap-1">
            {note.is_private && (
              <Chip>
                <EyeOffIcon className="size-3" aria-hidden="true" />
                {t('resources.conversation_notes.team_only')}
              </Chip>
            )}
            {!isClosed && note.priority && (
              <Chip>
                <NotePriorityIcon priority={note.priority} />
                {t(`resources.conversation_notes.priority.${note.priority}`)}
              </Chip>
            )}
            {!isClosed && assigneeName && (
              <Chip>
                <User01Icon className="size-3" aria-hidden="true" />
                {assigneeName}
              </Chip>
            )}
          </div>
        )}
      </div>

      {!isClosed &&
        note.replies?.map((noteReply) => (
          <ReplyBlock
            key={noteReply.id}
            reply={noteReply}
            canEdit={currentUserId === noteReply.created_by}
          />
        ))}

      <ReplyComposer
        value={reply}
        onChange={setReply}
        onSubmit={handleReply}
        placeholder={
          isClosed
            ? t('resources.conversations.inbox.notes.reopen_placeholder')
            : t('resources.conversations.inbox.notes.reply_placeholder')
        }
        sendLabel={t('resources.conversations.inbox.notes.send_reply')}
        isDisabled={!reply.trim() || createReply.isPending}
      />
    </article>
  )
}

export function InboxNotes({ conversationId, customerId }: InboxNotesProps) {
  const { t } = useTranslation()
  const [isCreating, setIsCreating] = useState(false)
  const { user: identity } = useIdentity()
  const { data: notes = [], isLoading } = useConversationNotes(conversationId)
  const { userTargets, teamTargets } = useAssignmentTargets(customerId)

  return (
    <>
      <section className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.conversations.inbox.tabs.notes')}
          </h3>
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            aria-label={t('resources.conversations.inbox.notes.create')}
            className="cursor-pointer rounded-md text-gray-900 hover:text-gray-700 dark:text-gray-100 dark:hover:text-gray-300"
          >
            <PlusIcon className="size-5" aria-hidden="true" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-sm text-gray-900 dark:text-gray-100">
            {t('resources.conversations.inbox.notes.empty')}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {notes.map((note) => (
              <NoteCard key={note.id} note={note} currentUserId={identity?.id} />
            ))}
          </div>
        )}
      </section>

      <CreateNoteModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onCreated={() => setIsCreating(false)}
        conversationId={conversationId}
        userTargets={userTargets}
        teamTargets={teamTargets}
      />
    </>
  )
}
